import { ScreenStepConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionConfig,
} from 'dashboard/models/verification-template-version';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { FieldSchema } from 'lib/fields';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import type { StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';

/**
 * should be synced with STEP_MODULES
 * in app/controllers/api/internal/dashboard/v1/inquiry_template_modules_controller.rb
 */
export enum ModuleOptionKey {
  CountrySelect = 'country_select',
  CountrySelectWithRouting = 'country_select_with_conditional_routing',
  Fail = 'fail',
  GovernmentId = 'government_id',
  GovernmentIdWithAutoclassification = 'government_id_with_autoclassification',
  Start = 'start',
  Success = 'success',
  Database = 'database',
  Document = 'document',
  DocumentHome = 'document_home',
  Selfie = 'selfie',
  Phone = 'phone',
  Screen = 'screen',
}

export interface InquiryTemplateModuleOption {
  key: ModuleOptionKey;
  'display-name': string;
  description: string;
  'is-initial-only'?: boolean;
  'is-terminal-only'?: boolean;
}

export interface ModulePreview {
  key: ModuleOptionKey;
  steps: ModulePreviewStep[];
  counts: ModulePreviewCounts;
}

export interface ModulePreviewStep {
  name: string;
  type: UiStepType;
  'client-data': StepPreview;
}
export interface ModulePreview {
  steps: ModulePreviewStep[];
  counts: ModulePreviewCounts;
}

export type ModulePreviewMap = Partial<Record<ModuleOptionKey, ModulePreview>>;
export interface ModulePreviewCounts {
  steps: number;
  actions: number;
  conditionals: number;
}

export type ModuleFieldSchema = FieldSchema & {
  description: string;
};

export interface ModuleScreen {
  // set type to screen steps until modules support complete (or other) steps
  'as-json': ScreenStepConfig;
  'client-data': StepPreview;
  description: string;
  name: string;
  'name-display': string;
  type: UiStepType;
}

export interface ModuleVerificationTemplateVersion {
  id: string;
  name: string;
  type: VerificationTemplateVersion['type'];
  config: VerificationTemplateVersionConfig;
}

export interface ModuleWorkflowVersion {
  id: string;
  name: string;
  description: string;
  'steps-config': WorkflowStep[];
}

export enum ModuleConnectionType {
  Screen = 'screen',
  WorkflowVersion = 'workflow_version',
}

/**
 * set incoming connection type to 'screen' until we have workflow-only or workflow-first
 * modules to simplify the type-checking and initial insert logic
 */
interface IncomingModuleConnection extends ModuleConnection {
  type: ModuleConnectionType.Screen;
}

export interface ModuleConnection {
  id: string; // step_name or workflow version token
  type: ModuleConnectionType;
}

export interface InquiryTemplateModuleAttributes {
  'incoming-connection': IncomingModuleConnection;
  'outgoing-connection'?: ModuleConnection; // terminal modules do not have outgoing connections
  'field-schemas': ModuleFieldSchema[];
  screens: ModuleScreen[];
  'verification-template-versions': ModuleVerificationTemplateVersion[];
  'workflow-versions': ModuleWorkflowVersion[];
}

export type InquiryTemplateModule = ApiResource<
  InquiryTemplateModuleAttributes,
  Record<string, never>,
  ApiModel.InquiryTemplateModule
>;
